// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";

import { isSet } from "utils";
import { IState } from "states/states";
import { IAction } from "actions";
import { ICharger, ChargerModel } from "parameter";
import { IStateDataCharger, ChargingState, PlugState } from "states/datastates";

import { ReactComponent as SvgCable } from "static/enlinecharger/charger_cable.svg";
import { ReactComponent as SvgLock } from "static/enlinecharger/charger_lock.svg";
import { ReactComponent as SvgCar } from "static/enlinecharger/charger_car.svg";
import { ReactComponent as ChargerCircontrol } from "static/enlinecharger/charger_circontrol.svg";
import { ReactComponent as ChargerKeba } from "static/enlinecharger/charger_keba.svg";
import { ReactComponent as ChargerGoe } from "static/enlinecharger/charger_goe.svg";
import { ReactComponent as ChargerEtrel } from "static/enlinecharger/charger_etrel.svg";
import { ReactComponent as ChargerBender } from "static/enlinecharger/charger_bender.svg";
import { ReactComponent as ChargerPce } from "static/enlinecharger/charger_pce.svg";

import cssContent from "content.scss";

interface IChargerViewExtProps {}

interface IChargerViewProps {
  chargerConfig: ICharger | undefined;
  chargerData: IStateDataCharger | undefined;
}

const mapStateToProps = (state: IState, ownProps: IChargerViewExtProps) => {
  const selectedCharger = state.router.charger; // for energy management chargers: the array index
  const props: IChargerViewProps = {
    chargerConfig: isSet(selectedCharger) ? state.parameter!.charger[selectedCharger] : undefined,
    chargerData: isSet(selectedCharger) ? state.data.charger?.[selectedCharger] : undefined,
  };
  return props;
};

interface IChargerViewDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargerViewDispatch = {};
  return dispatchProps;
};

class ChargerViewComponent extends React.Component<IChargerViewProps & IChargerViewDispatch> {
  public render() {
    const chargerData = this.props.chargerData;
    const styleFont: React.CSSProperties = { fontSize: 2.5 };
    const styleFontLarge: React.CSSProperties = { fontSize: 3 };
    const colorStatus =
      chargerData?.chargingState === ChargingState.charging
        ? cssContent.CHARGER_COLOR_CHARGING
        : cssContent.CHARGER_COLOR_UNDEFINED;
    const width =
      window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const isVertical = width <= parseInt(cssContent.CHARGER_IS_VERTICAL_LIMIT);

    return (
      <>
        <div className="chargerheading">{this.props.chargerConfig?.name}</div>
        <svg viewBox={isVertical ? "0 1 45 95" : "14 6 78 68"} version="1.1">
          <g transform={isVertical ? "translate(24.5, 5)" : "translate(36, 16)"} style={styleFont}>
            <g>
              <text dy="0 1 0 0 -1" textAnchor="end" transform="translate(-1)">
                Pmax =
              </text>
              <text id="PMax">{this.printNum(chargerData?.reqMaxPower, 0, "kW")}</text>
            </g>
            <g transform={isVertical ? "translate(0, 3.5)" : "translate(0, 4)"} style={styleFont}>
              <text dy="0 1 0 0 -1" dx="0 0.2 0" textAnchor="end" transform="translate(-1)">
                Imax =
              </text>
              <text id="IMax">{this.printNum(chargerData?.reqMaxCurrent, 0, "A")}</text>
            </g>
          </g>
          <g
            transform={
              isVertical ? "translate(11.5,-2.5) scale(0.5)" : "translate(46,6.5) scale(0.3)"
            }
          >
            {this.renderCharger()}
          </g>
          <g transform={isVertical ? "translate(21.5, 32)" : "translate(77, 15)"} style={styleFont}>
            <g transform="translate(0, 3)">
              <text dy="0 1 -1" textAnchor="end" transform="translate(-0.8)">
                E&Sigma; =
              </text>
              <text id="ETotal">{this.printNum(chargerData?.eTotal, 0, "kWh")}</text>
            </g>
          </g>
          <g
            transform={isVertical ? "translate(23, 42)" : "translate(58, 33)"}
            style={{ ...styleFontLarge, fill: colorStatus }}
          >
            <text id="chargingstate" textAnchor="middle" style={{ textTransform: "capitalize" }}>
              {isSet(chargerData?.chargingState) ? (
                <>
                  <FormattedMessage id={`chargingstate_${chargerData?.chargingState}`} />
                </>
              ) : (
                ""
              )}
            </text>
          </g>
          {this.props.chargerConfig?.type !== ChargerModel.ETREL && (
            <g
              id="phases"
              transform={isVertical ? "translate(9, 47)" : "translate(20, 40)"}
              style={styleFont}
            >
              {[0, 1, 2].map((phase) => {
                return (
                  <g
                    key={phase}
                    id={`L${phase + 1}`}
                    transform={`translate(0, ${(isVertical ? 3.2 : 3.5) * phase})`}
                    style={styleFont}
                  >
                    {this.renderPhase(phase)}
                    <g transform="translate(17.5, 0)">
                      <text id={`I${phase + 1}`} textAnchor="end">
                        {this.printNum(chargerData?.curI && chargerData.curI[phase], 1, "A")}
                      </text>
                    </g>
                    {chargerData?.curU && (
                      <g transform="translate(27, 0)">
                        <text id={`U${phase + 1}`} textAnchor="end">
                          {this.printNum(chargerData?.curU && chargerData.curU[phase], 0, "V")}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </g>
          )}
          {chargerData?.plugState !== PlugState.no_cable && (
            <g
              transform={
                isVertical ? "translate(15,45) scale(0.35)" : "translate(49,35) scale(0.22)"
              }
            >
              <SvgCable style={{ stroke: colorStatus, strokeWidth: 3 }} />
            </g>
          )}
          {chargerData?.chargingState &&
            [
              ChargingState.charging,
              ChargingState.charging_paused,
              ChargingState.charging_paused_by_EV,
            ].includes(chargerData.chargingState) && (
              <g
                transform={isVertical ? "translate(20, 73)" : "translate(76, 41.5)"}
                style={styleFontLarge}
              >
                <>
                  <text textAnchor="end" transform="translate(-0.8)">
                    P =
                  </text>
                  <text id="CurP">{this.printNum(chargerData?.curP, 1, "kW")}</text>
                </>
                <g transform="translate(0, 4)">
                  <text textAnchor="end" transform="translate(-0.8)">
                    E =
                  </text>
                  <text id="ESession">{this.printNum(chargerData?.eSession, 1, "kWh")}</text>
                </g>
              </g>
            )}
          {chargerData?.plugIsLocked && (
            <g
              transform={
                isVertical ? "translate(39,77) scale(0.08)" : "translate(75,52) scale(0.05)"
              }
            >
              <SvgLock />
            </g>
          )}
          {chargerData?.plugState === PlugState.cable_in_charger_and_car && (
            <g
              transform={isVertical ? "translate(0.5,40) scale(1)" : "translate(26,38) scale(0.7)"}
            >
              <SvgCar />
            </g>
          )}
        </svg>
      </>
    );
  }

  private renderCharger() {
    if (this.props.chargerConfig?.type === ChargerModel.KEBA) {
      return <ChargerKeba />;
    }
    if (this.props.chargerConfig?.type === ChargerModel.GOE_HOME) {
      return <ChargerGoe />;
    }
    if (this.props.chargerConfig?.type === ChargerModel.CIRCTRL) {
      return <ChargerCircontrol />;
    }
    if (this.props.chargerConfig?.type === ChargerModel.ETREL) {
      return <ChargerEtrel />;
    }
    if (this.props.chargerConfig?.type === ChargerModel.BENDER) {
      return <ChargerBender />;
    }
    if (this.props.chargerConfig?.type === ChargerModel.PCE_GL) {
      return <ChargerPce />;
    }
    // default
    return <ChargerKeba />;
  }

  private renderPhase(phaseNr: number) {
    return (
      <>
        <text dx={phaseNr === 0 ? "0 0.2 0" : "0"} textAnchor="end" transform="translate(7)">
          L{phaseNr + 1}
        </text>
        <g transform="scale(0.17) translate(-10, -16)">
          <line
            stroke={phaseNr === 0 ? "#000" : "#B3B3B3"}
            strokeWidth="3"
            strokeMiterlimit="10"
            x1="12"
            y1="0"
            x2="12"
            y2="14"
          />
          <line
            stroke={phaseNr === 1 ? "#000" : "#B3B3B3"}
            strokeWidth="3"
            strokeMiterlimit="10"
            x1="24"
            y1="20.5"
            x2="12"
            y2="13.5"
          />
          <line
            stroke={phaseNr === 2 ? "#000" : "#B3B3B3"}
            strokeWidth="3"
            strokeMiterlimit="10"
            x1="0"
            y1="20.5"
            x2="12"
            y2="13.5"
          />
        </g>
      </>
    );
  }

  private printNum(value: number | undefined, digits: number, unit: string) {
    if (isSet(value)) {
      return `${value.toFixed(digits)} ${unit}`;
    }
    return "";
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ChargerViewComponent);
